/**
 * Component: LoginPage
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R01
 *
 * Login page with two-panel responsive layout.
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Eye, EyeOff, AlertCircle, Clock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { useAuthStore, useIsAuthenticated } from '@/stores/auth-store';
import { useFeatureFlags, useIsFeatureEnabled } from '@/contexts/FeatureFlagContext';

// Form validation schema
const loginSchema = z.object({
  username: z.string().min(1, 'Email address is required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAuthenticated = useIsAuthenticated();
  const { login, isLoading, error, clearError } = useAuthStore();
  const { refetch: refetchFlags } = useFeatureFlags();

  // AUTH-V1-R09: Detect inactivity timeout redirect
  const inactivityTimeout = useMemo(
    () => searchParams.get('reason') === 'inactivity',
    [searchParams]
  );

  const isSamlSsoEnabled = useIsFeatureEnabled('samlSso');
  const [showPassword, setShowPassword] = useState(false);
  const [rateLimitCountdown, setRateLimitCountdown] = useState<number | null>(null);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      void navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Handle rate limit countdown
  useEffect(() => {
    if (rateLimitCountdown === null || rateLimitCountdown <= 0) return;

    const timer = setInterval(() => {
      setRateLimitCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearError();
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [rateLimitCountdown, clearError]);

  // Parse rate limit from error message
  useEffect(() => {
    if (error?.includes('Too many attempts')) {
      const match = error.match(/(\d+)\s*seconds/);
      if (match && match[1]) {
        setRateLimitCountdown(parseInt(match[1], 10));
      }
    }
  }, [error]);

  const onSubmit = async (data: LoginFormValues): Promise<void> => {
    try {
      await login(data.username, data.password);
      // Refetch feature flags now that we're authenticated so
      // gated items like Browse appear immediately.
      void refetchFlags();
      void navigate('/', { replace: true });
    } catch {
      // Clear password field on error
      form.setValue('password', '');
      form.setFocus('password');
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    void form.handleSubmit(onSubmit)(e);
  };

  const togglePasswordVisibility = (): void => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="flex min-h-screen">
      {/* Left panel - Login form */}
      <div className="flex w-full flex-col justify-center px-6 py-12 lg:w-1/2 lg:px-12 xl:px-24">
        <div className="mx-auto w-full max-w-md">
          {/* Logo and title */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              Galen Portal
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Sign in to your account to continue
            </p>
          </div>

          {/* AUTH-V1-R09: Session inactivity timeout alert */}
          {inactivityTimeout && !error && (
            <Alert variant="default" className="mb-6 border-amber-500 text-amber-700">
              <Clock className="h-4 w-4" />
              <AlertTitle>Session Expired</AlertTitle>
              <AlertDescription>
                Your session has expired due to inactivity. Please sign in again.
              </AlertDescription>
            </Alert>
          )}

          {/* Error alert */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Authentication Error</AlertTitle>
              <AlertDescription>
                {rateLimitCountdown !== null
                  ? `Too many attempts. Please try again in ${rateLimitCountdown} seconds.`
                  : error}
              </AlertDescription>
            </Alert>
          )}

          {/* Login form */}
          <Form {...form}>
            <form onSubmit={handleFormSubmit} className="space-y-6">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email address</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        autoComplete="email"
                        disabled={isLoading}
                        placeholder="Enter your email address"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          disabled={isLoading}
                          placeholder="Enter your password"
                          className="pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                          onClick={togglePasswordVisibility}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          tabIndex={-1}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || rateLimitCountdown !== null}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>
          </Form>

          {/* SAML SSO button — only visible when samlSso feature flag is enabled (AUTH-V1-R02) */}
          {isSamlSsoEnabled && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => {
                  window.location.href = '/api/auth/saml/login';
                }}
                disabled={isLoading}
              >
                Sign in with ESCardio SSO
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Right panel - Gradient background (hidden on mobile) */}
      <div className="hidden lg:block lg:w-1/2">
        <div className="flex h-full items-center justify-center bg-gradient-to-br from-primary to-primary/70">
          <div className="px-12 text-center">
            <h2 className="text-3xl font-bold text-primary-foreground">
              AI-Assisted Clinical Guidelines
            </h2>
            <p className="mt-4 text-lg text-primary-foreground/80">
              Access evidence-based clinical guidelines with intelligent search
              and retrieval powered by advanced AI.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
